import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getOrders, getTransactions } from '../../actions/downloads';
import { 
    Download, 
    FileSpreadsheet, 
    Calendar, 
    Monitor, 
    FileText, 
    AlertTriangle,
    CheckCircle,
    RefreshCw,
    Filter,
    Database,
    Activity,
    Clock
} from 'lucide-react';

import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";

const Downloads = ({ orders, getOrders, transactions, getTransactions, loading }) => {
    const initialState = {
        vendingMachine: '',
        startDate: '',
        endDate: '',
        reportType: ''
    };

    const [formData, setFormData] = useState(initialState);
    const [showAlertState, setShowAlertState] = useState(false);
    const [alertType, setAlertType] = useState('');
    const [alertMessage, setAlertMessage] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [downloadHistory, setDownloadHistory] = useState([]);

    const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const fileExtension = ".xlsx";

    useEffect(() => {
        const exportToCSV = (apiData, type) => {
            const ws = XLSX.utils.json_to_sheet(apiData);
            const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
            const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
            const data = new Blob([excelBuffer], { type: fileType });
            
            const fileName = generateFileName(type);
            FileSaver.saveAs(data, fileName + fileExtension);
            
            // Add to download history
            const newDownload = {
                id: Date.now(),
                fileName: fileName + fileExtension,
                type: type,
                date: new Date().toISOString(),
                recordCount: apiData.length
            };
            setDownloadHistory(prev => [newDownload, ...prev.slice(0, 4)]); // Keep last 5 downloads
            
            showSuccess(`${type} report downloaded successfully! (${apiData.length} records)`);
            setIsGenerating(false);
        };

        if(orders.length > 0){
            exportToCSV(orders, 'Order');
        }
        if(transactions.length > 0){
            exportToCSV(transactions, 'Transaction');
        }
    }, [orders, transactions]);

    const generateFileName = (type) => {
        return [
            type || formData.reportType,
            formatDate(new Date(formData.startDate)),
            "to",
            formatDate(new Date(formData.endDate)),
            "downloaded",
            formatDate(new Date())
        ].join('-');
    };

    const showAlert = (type, message) => {
        setAlertType(type);
        setAlertMessage(message);
        setShowAlertState(true);
        setTimeout(() => setShowAlertState(false), 5000);
    };

    const showSuccess = (message) => showAlert('success', message);
    const showError = (message) => showAlert('error', message);

    const { vendingMachine, startDate, endDate, reportType } = formData;

    const onChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        
        const startDateMs = new Date(formData.startDate).getTime();
        const endDateMs = new Date(formData.endDate).getTime();
        
        if(endDateMs < startDateMs){
            showError("End date cannot be earlier than start date");
            return;
        }

        setIsGenerating(true);
        
        try {
            if(formData.reportType === "transaction"){
                await getTransactions(formData.vendingMachine, formData.startDate, formData.endDate);
            }
            if(formData.reportType === "order"){
                await getOrders(formData.vendingMachine, formData.startDate, formData.endDate);
            }
        } catch (error) {
            showError("Failed to generate report. Please try again.");
            setIsGenerating(false);
        }
    };

    const formatDate = (date) => {
        let month = '' + (date.getMonth() + 1);
        let day = '' + date.getDate();
        let year = date.getFullYear();
        
        if (month.length < 2) month = '0' + month;
        if (day.length < 2) day = '0' + day;
        return [year, month, day].join('-');
    };

    const formatDateDisplay = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getReportIcon = (type) => {
        switch(type.toLowerCase()) {
            case 'order': return <FileText size={16} className="text-primary" />;
            case 'transaction': return <Activity size={16} className="text-success" />;
            default: return <FileSpreadsheet size={16} className="text-info" />;
        }
    };

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Alert Messages */}
                {showAlertState && (
                    <div className={`alert alert-${alertType === 'error' ? 'danger' : 'success'} alert-dismissible fade show mb-4`}>
                        <div className="d-flex align-items-center">
                            {alertType === 'error' ? 
                                <AlertTriangle size={20} className="me-2" /> : 
                                <CheckCircle size={20} className="me-2" />
                            }
                            {alertMessage}
                        </div>
                        <button 
                            type="button" 
                            className="btn-close" 
                            onClick={() => setShowAlertState(false)}
                        ></button>
                    </div>
                )}

                {/* Header Section */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <h1 className="h3 mb-2 text-gray-800 fw-bold d-flex align-items-center">
                                    <Download size={32} className="me-3 text-primary" />
                                    Download Reports
                                </h1>
                                <p className="text-muted mb-0">
                                    Generate and download Excel reports for orders and transactions
                                </p>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                {loading && (
                                    <div className="d-flex align-items-center text-muted">
                                        <RefreshCw size={16} className="me-2 spinner-border spinner-border-sm" />
                                        Loading...
                                    </div>
                                )}
                                <span className="badge bg-primary fs-6">
                                    {downloadHistory.length} Recent Download{downloadHistory.length !== 1 ? 's' : ''}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    {/* Download Form */}
                    <div className="col-lg-8">
                        <div className="card shadow-sm">
                            <div className="card-header bg-primary text-white">
                                <h5 className="mb-0 d-flex align-items-center">
                                    <Filter size={20} className="me-2" />
                                    Generate Report
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="alert alert-info d-flex align-items-start mb-4">
                                    <Database size={20} className="me-2 mt-1 flex-shrink-0" />
                                    <div>
                                        <strong>Pro Tip:</strong> To download reports across all vending machines, 
                                        enter <code>"all"</code> in the Vending Machine field.
                                    </div>
                                </div>

                                <form onSubmit={onSubmit}>
                                    <div className="row g-4">
                                        {/* Vending Machine */}
                                        <div className="col-md-6">
                                            <label className="form-label fw-bold">
                                                <Monitor size={16} className="me-2 text-primary" />
                                                Vending Machine ID *
                                            </label>
                                            <input
                                                className="form-control"
                                                type="text"
                                                placeholder="Enter machine ID or 'all' for all machines"
                                                name="vendingMachine"
                                                value={vendingMachine}
                                                onChange={onChange}
                                                required
                                            />
                                            <div className="form-text">
                                                Use "all" to generate reports for all vending machines
                                            </div>
                                        </div>

                                        {/* Report Type */}
                                        <div className="col-md-6">
                                            <label className="form-label fw-bold">
                                                <FileSpreadsheet size={16} className="me-2 text-success" />
                                                Report Type *
                                            </label>
                                            <select
                                                className="form-select"
                                                name="reportType"
                                                value={reportType}
                                                onChange={onChange}
                                                required
                                            >
                                                <option value="">Select Report Type</option>
                                                <option value="order">📋 Order Reports</option>
                                                <option value="transaction">💳 Transaction Reports</option>
                                            </select>
                                        </div>

                                        {/* Start Date */}
                                        <div className="col-md-6">
                                            <label className="form-label fw-bold">
                                                <Calendar size={16} className="me-2 text-info" />
                                                Start Date *
                                            </label>
                                            <input
                                                className="form-control"
                                                type="date"
                                                name="startDate"
                                                value={startDate}
                                                onChange={onChange}
                                                required
                                            />
                                        </div>

                                        {/* End Date */}
                                        <div className="col-md-6">
                                            <label className="form-label fw-bold">
                                                <Calendar size={16} className="me-2 text-warning" />
                                                End Date *
                                            </label>
                                            <input
                                                className="form-control"
                                                type="date"
                                                name="endDate"
                                                value={endDate}
                                                onChange={onChange}
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* Submit Button */}
                                    <div className="mt-4 text-center">
                                        <button 
                                            type="submit" 
                                            className="btn btn-primary btn-lg px-5"
                                            disabled={isGenerating}
                                        >
                                            {isGenerating ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                                    Generating Report...
                                                </>
                                            ) : (
                                                <>
                                                    <FileSpreadsheet size={20} className="me-2" />
                                                    Generate & Download Excel
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>

                    {/* Download History */}
                    <div className="col-lg-4">
                        <div className="card shadow-sm">
                            <div className="card-header bg-light">
                                <h5 className="mb-0 d-flex align-items-center">
                                    <Clock size={20} className="me-2 text-primary" />
                                    Recent Downloads
                                </h5>
                            </div>
                            <div className="card-body">
                                {downloadHistory.length > 0 ? (
                                    <div className="list-group list-group-flush">
                                        {downloadHistory.map((download) => (
                                            <div key={download.id} className="list-group-item border-0 px-0 py-3">
                                                <div className="d-flex align-items-center mb-2">
                                                    {getReportIcon(download.type)}
                                                    <span className="fw-bold ms-2">{download.fileName}</span>
                                                </div>
                                                <div className="small text-muted">
                                                    <div>📅 {formatDateDisplay(download.date)}</div>
                                                    <div>📊 {download.recordCount} records</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-4">
                                        <FileSpreadsheet size={48} className="text-muted mb-3" />
                                        <h6 className="text-muted">No Downloads Yet</h6>
                                        <p className="text-muted small mb-0">
                                            Your recent downloads will appear here
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Info Card */}
                        <div className="card shadow-sm mt-4">
                            <div className="card-body">
                                <h6 className="fw-bold text-primary mb-3">
                                    📋 Report Information
                                </h6>
                                <ul className="list-unstyled small text-muted mb-0">
                                    <li className="mb-2">
                                        <strong>Order Reports:</strong> Contains order details, customer info, and product data
                                    </li>
                                    <li className="mb-2">
                                        <strong>Transaction Reports:</strong> Includes payment details, transaction status, and amounts
                                    </li>
                                    <li className="mb-2">
                                        <strong>File Format:</strong> Excel (.xlsx) format for easy analysis
                                    </li>
                                    <li className="mb-0">
                                        <strong>Date Range:</strong> Maximum 90 days recommended for optimal performance
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

Downloads.propTypes = {
    getOrders: PropTypes.func.isRequired,
    orders: PropTypes.array,
    getTransactions: PropTypes.func.isRequired,
    transactions: PropTypes.array,
    loading: PropTypes.bool
};

const mapStateToProps = state => ({
    orders: state.downloads.orders,
    transactions: state.downloads.transactions,
    loading: state.downloads.loading
});

export default connect(mapStateToProps, { getOrders, getTransactions })(Downloads);